import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import {
  Container,
  Card,
  CardHeader,
  CardBody,
  Badge,
  Button,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Spinner,
} from "reactstrap";
import CommonHeader from "components/Headers/CommonHeader.js";
import {
  FaHeadset,
  FaPaperPlane,
  FaSearch,
  FaCheckDouble,
  FaComments,
  FaCircle,
  FaLock,
  FaLockOpen,
  FaEnvelope,
  FaPhoneAlt,
} from "react-icons/fa";
import { useSocket } from "context/SocketContext";
import {
  getSupportConversations,
  getSupportMessages,
  toggleSupportStatus,
  sendSupportMessage,
} from "api/ApiServices";
import { Image_Url } from "api/ApiClient";
import Swal from "sweetalert2";

const SupportReports = () => {
  const socket = useSocket();
  const location = useLocation();

  const [conversations, setConversations] = useState([]);
  const [activeConversationUuid, setActiveConversationUuid] = useState(
    location?.state?.conversationUuid || null
  );
  const [messages, setMessages] = useState([]);

  const [isLoadingConversations, setIsLoadingConversations] = useState(true);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [inputMessage, setInputMessage] = useState("");
  const [userIsTyping, setUserIsTyping] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [isSocketConnected, setIsSocketConnected] = useState(Boolean(socket?.connected));

  useEffect(() => {
    if (!socket) return;
    setIsSocketConnected(Boolean(socket.connected));

    const onConnect = () => setIsSocketConnected(true);
    const onDisconnect = () => setIsSocketConnected(false);

    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);
    socket.on("connect_error", onDisconnect);

    return () => {
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
      socket.off("connect_error", onDisconnect);
    };
  }, [socket]);

  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  // Active conversation object
  const activeConversation = conversations.find(
    (c) => c.conversationUuid === activeConversationUuid
  ) || null;

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, userIsTyping]);

  /**
   * Helper: Avatar image URL resolver
   */
  const getAvatarUrl = (user) => {
    const rawImage = user?.profileImage || user?.image || user?.avatar;
    const name = user?.fullName || user?.email || "User";
    const defaultAvatar = `https://ui-avatars.com/api/?name=${encodeURIComponent(
      name
    )}&background=5e72e4&color=fff&bold=true`;

    if (!rawImage || typeof rawImage !== "string" || !rawImage.trim()) {
      return defaultAvatar;
    }

    const trimmed = rawImage.trim();
    if (
      trimmed.startsWith("http://") ||
      trimmed.startsWith("https://") ||
      trimmed.startsWith("blob:")
    ) {
      return trimmed;
    }

    const baseUrl =
      Image_Url ||
      "https://areatrek-live-bucket.s3.us-east-2.amazonaws.com/users-profile-images/";
    const cleanBase = baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`;
    const cleanKey = trimmed.replace(/^\/+/, "");
    return `${cleanBase}${cleanKey}`;
  };

  /**
   * Helper: Date & time formatting
   */
  const formatTime = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return "";
    const now = new Date();
    const isToday = d.toDateString() === now.toDateString();
    if (isToday) {
      return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    }
    return `${d.toLocaleDateString([], {
      month: "short",
      day: "numeric",
    })} ${d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
  };

  const activeConversationUuidRef = useRef(activeConversationUuid);
  useEffect(() => {
    activeConversationUuidRef.current = activeConversationUuid;
  }, [activeConversationUuid]);

  /**
   * 1. Fetch Conversations from API
   */
  const fetchConversations = useCallback(async (isSilent = false) => {
    try {
      if (!isSilent) setIsLoadingConversations(true);
      const res = await getSupportConversations({
        page: 1,
        limit: 100,
        status: statusFilter,
      });

      if (res?.success && Array.isArray(res.data)) {
        setConversations(res.data);
        // Automatically select passed conversation from notification or first conversation if none selected yet
        setActiveConversationUuid((prev) => {
          const target = location?.state?.conversationUuid || prev;
          if (target && res.data.some((c) => c.conversationUuid === target)) {
            return target;
          }
          return res.data.length > 0 ? res.data[0].conversationUuid : null;
        });
      }
    } catch (err) {
      console.error("Failed to load support conversations:", err);
    } finally {
      if (!isSilent) setIsLoadingConversations(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  /**
   * 2. Fetch Messages for Active Conversation
   */
  const fetchMessages = useCallback(async (conversationUuid, isSilent = false) => {
    if (!conversationUuid) {
      setMessages([]);
      return;
    }
    try {
      if (!isSilent) setIsLoadingMessages(true);
      const res = await getSupportMessages(conversationUuid, {
        page: 1,
        limit: 100,
      });
      if (res?.success && Array.isArray(res.data)) {
        setMessages(res.data);
        if (res.conversation?.user) {
          setConversations((prev) =>
            prev.map((c) =>
              c.conversationUuid === conversationUuid
                ? { ...c, user: res.conversation.user }
                : c
            )
          );
        }
      }
    } catch (err) {
      console.error("Failed to load messages:", err);
    } finally {
      if (!isSilent) setIsLoadingMessages(false);
    }
  }, []);

  useEffect(() => {
    if (activeConversationUuid) {
      fetchMessages(activeConversationUuid);
    }
  }, [activeConversationUuid, fetchMessages]);

  /**
   * 3. Socket Room Management & Listeners
   */
  useEffect(() => {
    if (!socket) return;

    const joinActiveRoom = () => {
      const currentUuid = activeConversationUuidRef.current;
      if (currentUuid) {
        socket.emit("join_support_room", { conversationUuid: currentUuid });
        socket.emit("mark_support_read", { conversationUuid: currentUuid });
      }
    };

    if (activeConversationUuid) {
      joinActiveRoom();
    }

    // Always rejoin room on socket reconnect
    socket.on("connect", joinActiveRoom);

    return () => {
      socket.off("connect", joinActiveRoom);
      if (activeConversationUuid) {
        socket.emit("leave_support_room", {
          conversationUuid: activeConversationUuid,
        });
      }
    };
  }, [socket, activeConversationUuid]);

  useEffect(() => {
    if (!socket) return;

    const handleReceiveMessage = (newMsg) => {
      const currentActiveUuid = activeConversationUuidRef.current;

      // 1. If currently in this conversation room, append the new message
      if (newMsg.conversationUuid === currentActiveUuid) {
        setMessages((prev) => {
          if (
            prev.some(
              (m) =>
                m.messageUuid === newMsg.messageUuid ||
                (m._id && m._id === newMsg._id)
            )
          ) {
            return prev;
          }
          return [...prev, newMsg];
        });

        // Mark read immediately
        socket.emit("mark_support_read", {
          conversationUuid: newMsg.conversationUuid,
        });
      }

      // 2. Update conversation list preview & order
      setConversations((prev) => {
        const idx = prev.findIndex(
          (c) => c.conversationUuid === newMsg.conversationUuid
        );
        if (idx === -1) {
          fetchConversations(true);
          return prev;
        }

        const updated = [...prev];
        const conv = { ...updated[idx] };
        conv.lastMessage = newMsg.message;
        conv.lastMessageAt = newMsg.createdAt || new Date().toISOString();

        if (
          newMsg.conversationUuid !== currentActiveUuid &&
          newMsg.senderType === "user"
        ) {
          conv.adminUnreadCount = (conv.adminUnreadCount || 0) + 1;
        } else if (newMsg.conversationUuid === currentActiveUuid) {
          conv.adminUnreadCount = 0;
        }

        updated.splice(idx, 1);
        return [conv, ...updated];
      });
    };

    const handleConversationUpdated = (data) => {
      const currentActiveUuid = activeConversationUuidRef.current;
      setConversations((prev) => {
        const idx = prev.findIndex(
          (c) => c.conversationUuid === data.conversationUuid
        );
        if (idx === -1) {
          fetchConversations(true);
          return prev;
        }

        const updated = [...prev];
        const conv = { ...updated[idx] };
        conv.lastMessage = data.lastMessage;
        conv.lastMessageAt = data.lastMessageAt;
        if (data.conversationUuid !== currentActiveUuid) {
          conv.adminUnreadCount = data.adminUnreadCount;
        } else {
          conv.adminUnreadCount = 0;
        }

        updated.splice(idx, 1);
        return [conv, ...updated];
      });
    };

    const handleTyping = (data) => {
      if (
        data.conversationUuid === activeConversationUuidRef.current &&
        data.senderType === "user"
      ) {
        setUserIsTyping(Boolean(data.isTyping));
      }
    };

    const handleMessagesRead = (data) => {
      if (
        data.conversationUuid === activeConversationUuidRef.current &&
        data.readBy === "user"
      ) {
        setMessages((prev) =>
          prev.map((m) =>
            m.senderType === "admin" ? { ...m, isRead: true } : m
          )
        );
      }
    };

    socket.on("receive_support_message", handleReceiveMessage);
    socket.on("support_conversation_updated", handleConversationUpdated);
    socket.on("support_typing", handleTyping);
    socket.on("support_messages_read", handleMessagesRead);

    return () => {
      socket.off("receive_support_message", handleReceiveMessage);
      socket.off("support_conversation_updated", handleConversationUpdated);
      socket.off("support_typing", handleTyping);
      socket.off("support_messages_read", handleMessagesRead);
    };
  }, [socket, fetchConversations]);

  // Fallback auto-sync every 3.5 seconds + on window focus (runs silently in background)
  useEffect(() => {
    const syncInterval = setInterval(() => {
      if (activeConversationUuidRef.current) {
        fetchMessages(activeConversationUuidRef.current, true);
      }
      fetchConversations(true);
    }, 3500);

    const handleWindowFocus = () => {
      if (activeConversationUuidRef.current) {
        fetchMessages(activeConversationUuidRef.current, true);
      }
      fetchConversations(true);
    };

    window.addEventListener("focus", handleWindowFocus);

    return () => {
      clearInterval(syncInterval);
      window.removeEventListener("focus", handleWindowFocus);
    };
  }, [fetchMessages, fetchConversations]);

  /**
   * 4. Handle Input & Typing Notifications
   */
  const handleInputChange = (e) => {
    const val = e.target.value;
    setInputMessage(val);

    if (!socket || !activeConversationUuid) return;

    if (val.trim()) {
      socket.emit("support_typing_start", {
        conversationUuid: activeConversationUuid,
      });

      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => {
        socket.emit("support_typing_stop", {
          conversationUuid: activeConversationUuid,
        });
      }, 2000);
    } else {
      socket.emit("support_typing_stop", {
        conversationUuid: activeConversationUuid,
      });
    }
  };

  /**
   * 5. Send Admin Message
   */
  const handleSendMessage = async () => {
    if (!inputMessage.trim() || !activeConversationUuid) return;

    const text = inputMessage.trim();
    setInputMessage("");

    // If socket is actively connected, send via socket
    if (socket && socket.connected) {
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      socket.emit("support_typing_stop", {
        conversationUuid: activeConversationUuid,
      });

      socket.emit("send_support_message", {
        conversationUuid: activeConversationUuid,
        message: text,
        messageType: "text",
      });
    } else {
      // Fallback: Send via REST API if socket is disconnected or connecting
      try {
        const res = await sendSupportMessage(activeConversationUuid, {
          message: text,
          messageType: "text",
        });

        if (res?.success && res.data) {
          const newMsg = res.data;
          setMessages((prev) => {
            if (
              prev.some(
                (m) =>
                  m.messageUuid === newMsg.messageUuid ||
                  (m._id && m._id === newMsg._id)
              )
            ) {
              return prev;
            }
            return [...prev, newMsg];
          });

          // Update conversation preview in sidebar
          setConversations((prev) => {
            const idx = prev.findIndex(
              (c) => c.conversationUuid === activeConversationUuid
            );
            if (idx === -1) return prev;
            const updated = [...prev];
            const conv = { ...updated[idx] };
            conv.lastMessage = text;
            conv.lastMessageAt = new Date().toISOString();
            updated.splice(idx, 1);
            return [conv, ...updated];
          });
        } else {
          setInputMessage(text);
          Swal.fire({
            icon: "error",
            title: "Failed to send message",
            text: res?.message || "Please check your network connection.",
          });
        }
      } catch (err) {
        setInputMessage(text);
        Swal.fire({
          icon: "error",
          title: "Failed to send message",
          text: err.message || "Please check your network connection.",
        });
      }
    }
  };

  /**
   * 6. Toggle Ticket Status (Open <-> Closed)
   */
  const handleToggleStatus = async () => {
    if (!activeConversation) return;
    const isClosing = activeConversation.status === "open";
    const actionText = isClosing ? "close this ticket" : "reopen this ticket";

    const result = await Swal.fire({
      title: "Update Ticket Status?",
      text: `Are you sure you want to ${actionText}?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: isClosing ? "#f5365c" : "#2dce89",
      cancelButtonColor: "#8898aa",
      confirmButtonText: isClosing ? "Yes, Close Ticket" : "Yes, Reopen Ticket",
    });

    if (result.isConfirmed) {
      try {
        const res = await toggleSupportStatus(activeConversation.conversationUuid);
        if (res.success) {
          setConversations((prev) =>
            prev.map((c) =>
              c.conversationUuid === activeConversation.conversationUuid
                ? { ...c, status: res.data.status }
                : c
            )
          );
          Swal.fire({
            icon: "success",
            title: `Ticket ${res.data.status === "closed" ? "Closed" : "Reopened"}`,
            timer: 1500,
            showConfirmButton: false,
          });
        }
      } catch (err) {
        Swal.fire(
          "Error",
          err.message || "Failed to update ticket status",
          "error"
        );
      }
    }
  };

  // Filtered contacts list for client-side search (only users who have sent messages)
  const filteredConversations = conversations.filter((c) => {
    // Only show conversations that have actual messages sent
    if (!c.lastMessage || !c.lastMessage.trim()) {
      return false;
    }
    const q = searchQuery.toLowerCase();
    const name = c.user?.fullName?.toLowerCase() || "";
    const email = c.user?.email?.toLowerCase() || "";
    const mobile = c.user?.mobileNumber?.toLowerCase() || "";
    const lastMsg = c.lastMessage?.toLowerCase() || "";
    return (
      name.includes(q) ||
      email.includes(q) ||
      mobile.includes(q) ||
      lastMsg.includes(q)
    );
  });

  return (
    <>
      <CommonHeader />
      <Container className="mt--7" fluid>
        <Card className="shadow border-0 overflow-hidden" style={{ minHeight: "680px" }}>
          {/* Card Header with Live Socket Badge */}
          <CardHeader className="bg-white border-bottom py-3">
            <div className="d-flex align-items-center justify-content-between flex-wrap">
              <div className="d-flex align-items-center my-1">
                <div
                  className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center mr-3 shadow-sm"
                  style={{ width: "42px", height: "42px" }}
                >
                  <FaHeadset size={20} />
                </div>
                <div>
                  <div className="d-flex align-items-center">
                    <h3 className="mb-0 font-weight-bold text-dark">
                      Help & Support Live Chat
                    </h3>
                    <Badge
                      color={isSocketConnected ? "success" : "warning"}
                      pill
                      className="ml-2 font-weight-bold"
                      style={{ fontSize: "11px", letterSpacing: "0.3px" }}
                    >
                      <FaCircle
                        size={7}
                        className="mr-1"
                        style={{ verticalAlign: "middle" }}
                      />
                      {isSocketConnected ? "Live Socket" : "Socket Connecting..."}
                    </Badge>
                  </div>
                  <small className="text-muted">
                    Real-time support inbox for mobile travelers and guides
                  </small>
                </div>
              </div>

              <div className="d-flex align-items-center my-1">
                {/* Modern Segmented Status Filter */}
                <div
                  className="p-1 rounded-pill d-inline-flex align-items-center shadow-xs"
                  style={{ backgroundColor: "#f4f5f9", border: "1px solid #e9ecef" }}
                >
                  {[
                    { key: "all", label: "All" },
                    { key: "open", label: "Open" },
                    { key: "closed", label: "Closed" },
                  ].map(({ key, label }) => (
                    <button
                      key={key}
                      type="button"
                      className="btn btn-sm rounded-pill border-0 shadow-none"
                      style={{
                        fontSize: "12px",
                        padding: "5px 16px",
                        fontWeight: statusFilter === key ? 600 : 500,
                        backgroundColor: statusFilter === key ? "#5e72e4" : "transparent",
                        color: statusFilter === key ? "#ffffff" : "#6c757d",
                        boxShadow: statusFilter === key ? "0 2px 6px rgba(94, 114, 228, 0.3)" : "none",
                        transition: "all 0.15s ease",
                      }}
                      onClick={() => setStatusFilter(key)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </CardHeader>

          <CardBody className="p-0">
            <div className="d-flex flex-column flex-md-row" style={{ height: "620px" }}>
              {/* =====================================================
                  LEFT: CONVERSATION INBOX LIST
              ===================================================== */}
              <div
                className="border-right bg-white d-flex flex-column"
                style={{
                  flex: "0 0 340px",
                  height: "100%",
                }}
              >
                {/* Search Contacts */}
                <div className="p-3 border-bottom bg-white">
                  <div
                    className="d-flex align-items-center px-3 py-1 rounded-pill"
                    style={{ backgroundColor: "#f8f9fa", border: "1px solid #e9ecef" }}
                  >
                    <FaSearch size={12} className="text-muted mr-2 flex-shrink-0" />
                    <Input
                      type="search"
                      className="border-0 bg-transparent p-0 shadow-none"
                      placeholder="Search users or messages..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      style={{ fontSize: "12.5px", height: "30px", boxShadow: "none" }}
                    />
                  </div>
                </div>

                {/* Conversation List Stream */}
                <div className="flex-grow-1 overflow-auto">
                  {isLoadingConversations ? (
                    <div className="text-center py-5">
                      <Spinner color="primary" size="sm" />
                      <p className="text-muted small mt-2 mb-0">
                        Loading conversations...
                      </p>
                    </div>
                  ) : filteredConversations.length > 0 ? (
                    filteredConversations.map((conv) => {
                      const isSelected =
                        conv.conversationUuid === activeConversationUuid;
                      const user = conv.user || {};
                      const unread = conv.adminUnreadCount || 0;
                      const isOpen = conv.status === "open";

                      return (
                        <div
                          key={conv.conversationUuid}
                          className="p-3 border-bottom cursor-pointer"
                          style={{
                            backgroundColor: isSelected ? "#f0f4ff" : "#ffffff",
                            borderLeftWidth: isSelected ? "4px" : "0px",
                            borderLeftColor: "#5e72e4",
                            borderLeftStyle: "solid",
                            transition: "all 0.15s ease",
                            cursor: "pointer",
                          }}
                          onClick={() => {
                            setActiveConversationUuid(conv.conversationUuid);
                            // Mark unread as 0 locally
                            setConversations((prev) =>
                              prev.map((c) =>
                                c.conversationUuid === conv.conversationUuid
                                  ? { ...c, adminUnreadCount: 0 }
                                  : c
                              )
                            );
                          }}
                        >
                          <div className="d-flex align-items-center">
                            <div className="position-relative mr-2 flex-shrink-0">
                              <img
                                src={getAvatarUrl(user)}
                                alt={user.fullName || "User"}
                                className="rounded-circle shadow-sm"
                                style={{
                                  width: "42px",
                                  height: "42px",
                                  objectFit: "cover",
                                }}
                                onError={(e) => {
                                  e.currentTarget.onerror = null;
                                  e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                                    user.fullName || user.email || "User"
                                  )}&background=5e72e4&color=fff&bold=true`;
                                }}
                              />
                              <span
                                className={`position-absolute rounded-circle border border-white ${
                                  isOpen ? "bg-success" : "bg-danger"
                                }`}
                                style={{
                                  width: "11px",
                                  height: "11px",
                                  bottom: "1px",
                                  right: "1px",
                                }}
                                title={isOpen ? "Ticket Open" : "Ticket Closed"}
                              />
                            </div>

                            <div className="flex-grow-1 text-truncate" style={{ minWidth: 0 }}>
                              <div className="d-flex justify-content-between align-items-center">
                                <span
                                  className={`text-truncate ${
                                    unread > 0 ? "font-weight-bold" : "font-weight-600"
                                  } text-dark`}
                                  style={{ fontSize: "13px", maxWidth: "150px" }}
                                >
                                  {user.fullName || user.email || "Support User"}
                                </span>
                                <small
                                  className="text-muted ml-1 flex-shrink-0"
                                  style={{ fontSize: "10px" }}
                                >
                                  {formatTime(conv.lastMessageAt || conv.createdAt)}
                                </small>
                              </div>

                              <div className="d-flex justify-content-between align-items-center mt-1">
                                <p
                                  className={`mb-0 text-truncate ${
                                    unread > 0
                                      ? "font-weight-bold text-dark"
                                      : "text-muted"
                                  }`}
                                  style={{ fontSize: "12px", maxWidth: "180px" }}
                                >
                                  {conv.lastMessage || "No messages yet"}
                                </p>

                                <div className="d-flex align-items-center ml-1">
                                  {unread > 0 && (
                                    <Badge
                                      color="danger"
                                      pill
                                      className="py-1 px-2 mr-1"
                                      style={{ fontSize: "10px" }}
                                    >
                                      {unread}
                                    </Badge>
                                  )}
                                  <span
                                    className="badge badge-pill"
                                    style={{
                                      fontSize: "9px",
                                      padding: "2px 7px",
                                      fontWeight: 700,
                                      letterSpacing: "0.4px",
                                      textTransform: "uppercase",
                                      backgroundColor: isOpen ? "#e6fffa" : "#edf2f7",
                                      color: isOpen ? "#047481" : "#718096",
                                      border: `1px solid ${isOpen ? "#b2f5ea" : "#e2e8f0"}`,
                                    }}
                                  >
                                    {isOpen ? "Open" : "Closed"}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-center py-5 text-muted small">
                      <FaComments size={28} className="text-muted opacity-5 mb-2 d-block mx-auto" />
                      No support conversations found
                    </div>
                  )}
                </div>
              </div>

              {/* =====================================================
                  RIGHT: LIVE CHAT PANE
              ===================================================== */}
              <div
                className="flex-grow-1 d-flex flex-column bg-light"
                style={{ height: "100%", minWidth: 0 }}
              >
                {activeConversation ? (
                  <>
                    {/* Chat Box Header */}
                    <div className="p-3 bg-white border-bottom d-flex align-items-center justify-content-between shadow-xs">
                      <div className="d-flex align-items-center">
                        <div className="position-relative mr-3">
                          <img
                            src={getAvatarUrl(activeConversation.user)}
                            alt={activeConversation.user?.fullName || "User"}
                            className="rounded-circle shadow-sm"
                            style={{
                              width: "46px",
                              height: "46px",
                              objectFit: "cover",
                            }}
                            onError={(e) => {
                              e.currentTarget.onerror = null;
                              e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                                activeConversation.user?.fullName ||
                                  activeConversation.user?.email ||
                                  "User"
                              )}&background=5e72e4&color=fff&bold=true`;
                            }}
                          />
                          <span
                            className={`position-absolute rounded-circle border border-white ${
                              activeConversation.status === "open"
                                ? "bg-success"
                                : "bg-danger"
                            }`}
                            style={{
                              width: "12px",
                              height: "12px",
                              bottom: "1px",
                              right: "1px",
                            }}
                          />
                        </div>
                        <div>
                          <div className="d-flex align-items-center">
                            <h4 className="mb-0 font-weight-bold text-dark mr-2" style={{ fontSize: "16px" }}>
                              {activeConversation.user?.fullName || "Support User"}
                            </h4>
                            <span
                              className="badge badge-pill"
                              style={{
                                fontSize: "10px",
                                padding: "3px 10px",
                                fontWeight: 700,
                                letterSpacing: "0.4px",
                                textTransform: "uppercase",
                                backgroundColor:
                                  activeConversation.status === "open"
                                    ? "#e6fffa"
                                    : "#edf2f7",
                                color:
                                  activeConversation.status === "open"
                                    ? "#047481"
                                    : "#718096",
                                border: `1px solid ${
                                  activeConversation.status === "open"
                                    ? "#b2f5ea"
                                    : "#cbd5e0"
                                }`,
                              }}
                            >
                              {activeConversation.status === "open"
                                ? "Open Ticket"
                                : "Closed Ticket"}
                            </span>
                          </div>

                          {/* User Contact Details: Clear Email & Phone Number */}
                          <div className="d-flex align-items-center flex-wrap mt-1 text-muted" style={{ fontSize: "12px" }}>
                            {activeConversation.user?.email && (
                              <span className="d-inline-flex align-items-center mr-3" title="User Email Address">
                                <FaEnvelope size={11} className="mr-1 text-primary" style={{ opacity: 0.8 }} />
                                <span>{activeConversation.user.email}</span>
                              </span>
                            )}
                            {activeConversation.user?.mobileNumber && (
                              <span className="d-inline-flex align-items-center mr-3" title="User Mobile / Phone Number">
                                <FaPhoneAlt size={10} className="mr-1 text-success" style={{ opacity: 0.8 }} />
                                <span>{activeConversation.user.mobileNumber}</span>
                              </span>
                            )}
                            {activeConversation.user?.country && (
                              <span
                                className="badge badge-light border px-2 py-0 text-muted"
                                style={{ fontSize: "10px", fontWeight: 500 }}
                              >
                                {activeConversation.user.country}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Ticket Status Action Button */}
                      <div>
                        <Button
                          size="sm"
                          className="d-inline-flex align-items-center shadow-xs"
                          style={{
                            borderRadius: "20px",
                            fontWeight: 600,
                            fontSize: "12px",
                            padding: "6px 16px",
                            backgroundColor:
                              activeConversation.status === "open"
                                ? "#fff5f5"
                                : "#f0fff4",
                            borderColor:
                              activeConversation.status === "open"
                                ? "#feb2b2"
                                : "#9ae6b4",
                            color:
                              activeConversation.status === "open"
                                ? "#e53e3e"
                                : "#276749",
                            transition: "all 0.15s ease",
                          }}
                          onClick={handleToggleStatus}
                          title={
                            activeConversation.status === "open"
                              ? "Close Ticket"
                              : "Reopen Ticket"
                          }
                        >
                          {activeConversation.status === "open" ? (
                            <>
                              <FaLock className="mr-1" size={11} /> Close Ticket
                            </>
                          ) : (
                            <>
                              <FaLockOpen className="mr-1" size={11} /> Reopen Ticket
                            </>
                          )}
                        </Button>
                      </div>
                    </div>

                    {/* Chat Messages Body */}
                    <div
                      className="p-4 flex-grow-1 overflow-auto"
                      style={{
                        backgroundColor: "#f7f9fc",
                        display: "flex",
                        flexDirection: "column",
                        gap: "12px",
                      }}
                    >
                      {isLoadingMessages ? (
                        <div className="text-center py-5">
                          <Spinner color="primary" size="sm" />
                          <p className="text-muted small mt-2 mb-0">
                            Loading message history...
                          </p>
                        </div>
                      ) : messages.length === 0 ? (
                        <div className="text-center my-auto py-5 text-muted">
                          <div
                            className="rounded-circle d-flex align-items-center justify-content-center mx-auto mb-3 shadow-xs"
                            style={{
                              width: "56px",
                              height: "56px",
                              backgroundColor: "#eef2ff",
                            }}
                          >
                            <FaComments size={24} className="text-primary" />
                          </div>
                          <h5 className="font-weight-bold text-dark mb-1">
                            No messages exchanged yet
                          </h5>
                          <small className="text-muted">
                            Type a reply below to greet and assist {activeConversation.user?.fullName || "the user"}.
                          </small>
                        </div>
                      ) : (
                        messages.map((msg) => {
                          const isSupport = msg.senderType === "admin";

                          return (
                            <div
                              key={msg.messageUuid || msg._id || Math.random()}
                              className={`d-flex ${
                                isSupport
                                  ? "justify-content-end"
                                  : "justify-content-start"
                              }`}
                            >
                              {!isSupport && (
                                <img
                                  src={getAvatarUrl(activeConversation.user)}
                                  alt="User"
                                  className="rounded-circle mr-2 align-self-end shadow-sm"
                                  style={{
                                    width: "32px",
                                    height: "32px",
                                    objectFit: "cover",
                                  }}
                                  onError={(e) => {
                                    e.currentTarget.onerror = null;
                                    e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                                      activeConversation.user?.fullName ||
                                        activeConversation.user?.email ||
                                        "User"
                                    )}&background=5e72e4&color=fff&bold=true`;
                                  }}
                                />
                              )}

                              <div
                                style={{
                                  maxWidth: "68%",
                                  borderRadius: isSupport
                                    ? "18px 18px 4px 18px"
                                    : "18px 18px 18px 4px",
                                  padding: "10px 16px",
                                  background: isSupport
                                    ? "linear-gradient(135deg, #5e72e4 0%, #4338ca 100%)"
                                    : "#ffffff",
                                  color: isSupport ? "#ffffff" : "#2d3748",
                                  boxShadow: isSupport
                                    ? "0 3px 10px rgba(94, 114, 228, 0.22)"
                                    : "0 2px 6px rgba(0,0,0,0.05)",
                                  border: isSupport ? "none" : "1px solid #edf2f7",
                                }}
                              >
                                {!isSupport && (
                                  <div
                                    className="font-weight-bold mb-1"
                                    style={{
                                      fontSize: "11px",
                                      color: "#5e72e4",
                                    }}
                                  >
                                    {activeConversation.user?.fullName || "User"}
                                  </div>
                                )}

                                <p
                                  className="mb-1"
                                  style={{
                                    fontSize: "13.5px",
                                    lineHeight: "1.45",
                                    wordBreak: "break-word",
                                    whiteSpace: "pre-wrap",
                                  }}
                                >
                                  {msg.message}
                                </p>

                                <div
                                  className={`d-flex align-items-center ${
                                    isSupport
                                      ? "justify-content-end"
                                      : "justify-content-start"
                                  }`}
                                  style={{
                                    fontSize: "10px",
                                    color: isSupport
                                      ? "rgba(255, 255, 255, 0.75)"
                                      : "#a0aec0",
                                  }}
                                >
                                  <span>
                                    {formatTime(msg.createdAt)}
                                  </span>
                                  {isSupport && (
                                    <FaCheckDouble
                                      size={10}
                                      className="ml-1"
                                      color={msg.isRead ? "#6ee7b7" : "rgba(255, 255, 255, 0.75)"}
                                      title={msg.isRead ? "Read by user" : "Sent"}
                                    />
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })
                      )}

                      {/* Typing Indicator */}
                      {userIsTyping && (
                        <div className="d-flex align-items-center text-muted small ml-4 my-1">
                          <FaCircle
                            size={7}
                            className="mr-1 text-primary animate-pulse"
                          />
                          <span className="font-italic">
                            {activeConversation.user?.fullName || "User"} is typing...
                          </span>
                        </div>
                      )}

                      <div ref={messagesEndRef} />
                    </div>

                    {/* Chat Input Footer */}
                    <div className="p-3 bg-white border-top">
                      {activeConversation.status === "closed" ? (
                        <div
                          className="text-center py-2 px-3 rounded small d-flex align-items-center justify-content-center"
                          style={{
                            backgroundColor: "#f8f9fa",
                            border: "1px dashed #cbd5e0",
                            color: "#718096",
                          }}
                        >
                          <FaLock className="mr-2 text-warning" />
                          <span>This support ticket is closed.</span>
                          <Button
                            color="link"
                            size="sm"
                            className="p-0 ml-2 text-primary font-weight-bold"
                            onClick={handleToggleStatus}
                          >
                            Click here to reopen
                          </Button>
                        </div>
                      ) : (
                        <div
                          className="d-flex align-items-center p-1 pl-3 rounded-pill"
                          style={{
                            backgroundColor: "#f8f9fa",
                            border: "1px solid #e2e8f0",
                          }}
                        >
                          <Input
                            type="text"
                            placeholder="Type reply to user... (Press Enter to send)"
                            value={inputMessage}
                            onChange={handleInputChange}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                handleSendMessage();
                              }
                            }}
                            className="border-0 bg-transparent shadow-none p-0"
                            style={{
                              fontSize: "13px",
                              height: "36px",
                              boxShadow: "none",
                            }}
                          />

                          <Button
                            color="primary"
                            className="rounded-circle p-0 d-flex align-items-center justify-content-center shadow-sm ml-2"
                            style={{
                              width: "38px",
                              height: "38px",
                              flexShrink: 0,
                              background:
                                "linear-gradient(135deg, #5e72e4 0%, #4338ca 100%)",
                              border: "none",
                            }}
                            onClick={handleSendMessage}
                            disabled={!inputMessage.trim()}
                            title="Send reply"
                          >
                            <FaPaperPlane size={13} />
                          </Button>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="d-flex flex-column align-items-center justify-content-center h-100 text-muted p-4">
                    <div
                      className="rounded-circle bg-white shadow-sm d-flex align-items-center justify-content-center mb-3"
                      style={{ width: "70px", height: "70px" }}
                    >
                      <FaHeadset size={32} className="text-primary opacity-6" />
                    </div>
                    <h4 className="font-weight-bold text-dark mb-1">
                      No Conversation Selected
                    </h4>
                    <p className="small text-muted mb-0 text-center" style={{ maxWidth: "280px" }}>
                      Select a support conversation from the inbox on the left to start live chatting with the user.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </CardBody>
        </Card>
      </Container>
    </>
  );
};

export default SupportReports;
